/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import querystring from "querystring";
import {
  withRouter,
  type RouterHistory,
  type Location
} from "react-router-dom";
import Gettext from "../common/Gettext";
import { LangIcon } from "./Icons";
import * as Flags from "./IconsLanguage";
import SimpleSpinner from "../Spinners/SimpleSpinner";
import { fontSize, rtl } from "../common/styled-mixins";
import { gaReducer } from "../GA";

const LanguageView = styled.div`
  position: relative;
  font-size: 0;
  line-height: 0;
  z-index: 2;
`;

const MenuItem = styled.div`
  display: inline-block;
  vertical-align: top;
  width: 132px;
  margin-bottom: 3px;
  padding: 9px;
  ${rtl("padding-left", "14px")};
  ${rtl("padding-right", "11px")};
  font-size: ${fontSize(14)};
  line-height: 19px;
  text-decoration: none;
  white-space: nowrap;
  color: inherit;
  cursor: pointer;

  :hover {
    color: inherit;
    cursor: default;
  }
`;

const Container = styled.div`
  position: absolute;
  top: 2px;
  display: inline-block;
  vertical-align: top;
  width: 88px;
  font-size: ${fontSize(12, 12)};
  line-height: 19px;
  cursor: pointer;

  ${props =>
    props.active &&
    css`
      background-color: ${({ theme }) => theme.languageBackground};
      color: ${({ theme }) => theme.languageColor};
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.75);
    `};

  @media (max-width: 959px) {
    ${rtl("right", 0)};
    z-index: 1;
  }
`;

const Icon = styled.span`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 19px;
  height: 17px;
  ${rtl("margin-right", "10px")};
  color: ${({ theme }) => theme.staticHeaderLink};
`;

const Header = styled.div`
  width: 100%;
  padding-bottom: 6px;
  text-transform: uppercase;
  text-decoration: none;
  color: inherit;
  line-height: inherit;
  cursor: pointer;

  :hover {
    color: ${props => props.theme.alternativeAccent};
  }

  ${props =>
    props.active &&
    css`
      position: relative;

      :before {
        content: "";
        position: absolute;
        width: 75px;
        left: 50%;
        bottom: 0;
        transform: translateX(-50%);
        border-bottom: 1px solid ${props => props.theme.mainSeparator};
      }
    `};
`;

const HeaderInner = styled.span`
  position: relative;
  display: block;
  width: 100%;
  padding: 8px 10px 6px;
  ${rtl("text-align", "left")};
  backface-visibility: hidden;
`;

const Arrow = styled.div`
  position: absolute;
  top: 50%;
  ${rtl("right", "11px")};
  transform: translateY(-50%) rotate(0deg);
  transform-origin: 50%;
  border: 4px solid transparent;
  border-top: 6px solid currentColor;
  border-bottom: none;
  transition: transform 0.3s ease;
  backface-visibility: hidden;

  ${props =>
    props.active &&
    css`
      transform: translateY(-50%) rotate(-180deg);
      transform-origin: 50%;
    `};
`;

const Item = styled.div`
  position: relative;
  display: block;
  width: 100%;
  padding: 13px 10px;
  line-height: 1;
  ${rtl("text-align", "left")};
  text-transform: uppercase;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  :hover,
  :focus,
  :active {
    color: ${props => props.theme.alternativeAccent};
  }

  :visited {
    color: ${({ theme }) => theme.staticHeaderLink};
  }

  ${props =>
    props.active &&
    css`
      background-color: ${props => props.theme.main};
    `};
`;

const langMap = [
  {
    key: "en",
    title: "ENG",
    icon: <Flags.ENG />
  },
  {
    key: "ru",
    title: "RUS",
    icon: <Flags.RUS />
  },
  {
    key: "vn",
    title: "VNM",
    icon: <Flags.VNM />
  },
  {
    key: "ph",
    title: "PHL",
    icon: <Flags.PHL />
  },
  {
    key: "th",
    title: "THA",
    icon: <Flags.THA />
  },
  {
    key: "id",
    title: "IDN",
    icon: <Flags.IDN />
  },
  {
    key: "hi",
    title: "HIN",
    icon: <Flags.HIN />
  },
  {
    key: "ar",
    title: "ARA",
    icon: <Flags.ARA />
  },
  {
    key: "es",
    title: "ESP",
    icon: <Flags.ESP />
  },
  {
    key: "cn",
    title: "中文",
    icon: <Flags.CHN />
  },
  {
    key: "tr",
    title: "TUR",
    icon: <Flags.TUR />
  }
];

const defaultLang = langMap[0];

function LangItem(props) {
  return (
    <Item onClick={props.onClick} active={props.active === props.locale}>
      {props.title} {props.Icon}
    </Item>
  );
}

type SelectProps = {
  history: RouterHistory,
  location: Location,
  locales: Array<string>,
  locale: string | null,
  showLoader: boolean,
  onLocaleChange: (value: string) => void,
  className?: string,
  HeaderComponent?: React.StatelessFunctionalComponent<*>
};

type SelectState = {
  isLangOpen: boolean
};

class RoutelessLanguageSelect extends React.Component<
  SelectProps,
  SelectState
> {
  state = {
    isLangOpen: false
  };

  toggleLang = () => {
    this.setState({ isLangOpen: !this.state.isLangOpen });
  };

  chooseLang = (key: string) => {
    this.toggleLang();

    const query = querystring.parse(this.props.location.search.slice(1));

    if (typeof query.lang === "string") {
      this.props.history.replace({
        ...this.props.location,
        search: querystring.stringify({
          ...query,
          lang: key
        })
      });
    }
    gaReducer("CLICKBTN_CHANGE_LANG", {
      lang: key
    });
    this.props.onLocaleChange(key);
  };

  render() {
    const {
      locales,
      showLoader,
      className,
      HeaderComponent = Header
    } = this.props;

    const query = querystring.parse(this.props.location.search.slice(1));
    const locale =
      query.lang != null
        ? query.lang === "vi"
          ? "vn"
          : query.lang === "tl"
          ? "ph"
          : query.lang
        : this.props.locale;

    if (!locales) return false;

    const availableLanguages = langMap.filter(el => locales.includes(el.key));
    const activeLang =
      availableLanguages.find(el => el.key === locale) || defaultLang;

    return (
      <Container className={className} active={this.state.isLangOpen}>
        {showLoader ? (
          <div
            style={{
              paddingTop: 7,
              textAlign: "center"
            }}
          >
            <SimpleSpinner />
          </div>
        ) : (
          <HeaderComponent
            active={this.state.isLangOpen}
            onClick={this.toggleLang}
          >
            <HeaderInner>
              {activeLang && activeLang.title} {activeLang && activeLang.icon}
              <Arrow active={this.state.isLangOpen} />
            </HeaderInner>
          </HeaderComponent>
        )}
        {activeLang && (
          <div
            style={{
              overflow: "hidden",
              height: this.state.isLangOpen ? "100%" : 0
            }}
          >
            {availableLanguages.map(lang => {
              const { key, title, icon } = lang;

              return key === activeLang.key ? null : (
                <LangItem
                  key={key}
                  locale={key}
                  title={title}
                  Icon={icon}
                  onClick={() => {
                    this.chooseLang(key);
                  }}
                  active={locale}
                />
              );
            })}
          </div>
        )}
      </Container>
    );
  }
}

export const LangSelect = withRouter(
  (RoutelessLanguageSelect: React.ComponentType<SelectProps>)
);

type Props = {
  showLoader: boolean,
  locales: Array<string>,
  locale: string | null,
  onLocaleChange: (value: string) => void,
  hideLangSelect?: boolean
};

function Language(props: Props) {
  if (props.hideLangSelect) return null;

  return (
    <LanguageView>
      <MenuItem>
        <Icon>
          <LangIcon />
        </Icon>
        <Gettext>Languages</Gettext>:
      </MenuItem>
      <LangSelect
        locale={props.locale}
        locales={props.locales}
        onLocaleChange={props.onLocaleChange}
        showLoader={props.showLoader}
      />
    </LanguageView>
  );
}

export default Language;



// WEBPACK FOOTER //
// ./src/components/Navigation/Language.js